import 'core-js';
import 'regenerator-runtime/runtime';

import { noConflict, FeatureGroup, Point } from 'leaflet';
import { select } from 'd3-selection';
import { line, curveMonotoneX } from 'd3-shape';
import Set from 'es6-set';
import WeakMap from 'es6-weak-map';
import Symbol from 'es6-symbol';
import { updateFor } from './helpers/Layer';
import { createFor, removeFor, clearFor } from './helpers/Polygon';
import { CREATE, EDIT, DELETE, APPEND, EDIT_APPEND, NONE, ALL, modeFor } from './helpers/Flags';
import simplifyPolygon from './helpers/Simplify';
import Hammer from 'hammerjs';

// Preventing binding to the `window`.
noConflict();

const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

/**
 * @constant polygons
 * @type {WeakMap}
 */
export const polygons = new WeakMap();

/**
 * @constant defaultOptions
 * @type {Object}
 */
export const defaultOptions = {
    mode: ALL,
    smoothFactor: 0.3,
    elbowDistance: 10,
    simplifyFactor: 1.1,
    mergePolygons: true,
    concavePolygon: true,
    maximumPolygons: Infinity,
    notifyAfterEditExit: false,
    leaveModeAfterCreate: false,
    strokeWidth: 2
};

/**
 * @constant instanceKey
 * @type {Symbol}
 */
export const instanceKey = Symbol('freedraw/instance');

/**
 * @constant modesKey
 * @type {Symbol}
 */
export const modesKey = Symbol('freedraw/modes');

/**
 * @constant notifyDeferredKey
 * @type {Symbol}
 */
export const notifyDeferredKey = Symbol('freedraw/notify-deferred');

/**
 * @constant edgesKey
 * @type {Symbol}
 */
export const edgesKey = Symbol('freedraw/edges');

/**
 * @constant cancelKey
 * @type {Symbol}
 */
const cancelKey = Symbol('freedraw/cancel');

export default class FreeDraw extends FeatureGroup {

    /**
     * @constructor
     * @param {Object} [options = {}]
     * @return {void}
     */
    constructor(options = defaultOptions) {
        super();
        this.options = { ...defaultOptions, ...options };
        this.lines = [];
        this.isTouchDevice = isTouchDevice();
        this.mouseDownHandler = this.mouseDown.bind(this);
        this.mouseMoveHandler = this.mouseMove.bind(this);
        this.mouseUpHandler = this.mouseUp.bind(this);

    }

    /**
     * @method onAdd
     * @param {Object} map
     * @return {void}
     */
    onAdd(map) {

        // Memorise the map instance.
        this.map = map;

        // Attach the cancel function and the instance to the map.
        map[cancelKey] = () => {};
        map[instanceKey] = this;
        map[notifyDeferredKey] = () => {};

        // Setup the dependency injection for simplifying the polygon.
        map.simplifyPolygon = simplifyPolygon;

        // Add the item to the map.
        polygons.set(map, new Set());

        // Set the initial mode.
        modeFor(map, this.options.mode, this.options);

        // Instantiate the SVG layer that sits on top of the map.
        const svg = this.svg = select(map._container).append('svg')
                                 .classed('free-draw', true).attr('width', '100%').attr('height', '100%')
                                 .style('pointer-events', 'none').style('z-index', '1001').style('position', 'relative');

        // Configure Hammer.js only for Gesture Devices.
        // this.hammer = new Hammer(this.svg.node());
        const hammer = new Hammer(map.getContainer());
        hammer.get('pinch').set({ enable: true });
        hammer.get('swipe').set({ direction: Hammer.DIRECTION_ALL });
        // Set the mouse events.
        this.listenForEvents(hammer, map, svg, this.options);

    }

    /**
     * @method onRemove
     * @param {Object} map
     * @return {void}
     */
    onRemove(map) {

        // Remove the item from the map.
        polygons.delete(map);

        // Remove the SVG layer.
        this.svg.remove();

        // Remove the appendages from the map container.
        delete map[cancelKey];
        delete map[instanceKey];
        delete map.simplifyPolygon;

    }

    /**
     * @method create
     * @param {LatLng[]} latLngs
     * @param {Object} [options = { concavePolygon: false }]
     * @return {Object}
     */
    create(latLngs, options = { concavePolygon: false }) {
        const created = createFor(this.map, latLngs, { ...this.options, ...options });
        updateFor(this.map, 'create');
        return created;
    }

    /**
     * @method remove
     * @param {Object} polygon
     * @return {void}
     */
    remove(polygon) {
        polygon ? removeFor(this.map, polygon) : super.remove();
        updateFor(this.map, 'remove');
    }

    /**
     * @method clear
     * @return {void}
     */
    clear() {
        clearFor(this.map);
        updateFor(this.map, 'clear');
    }

    /**
     * @method setMode
     * @param {Number} [mode = null]
     * @return {Number}
     */
    mode(mode = null) {

        // Set mode when passed `mode` is numeric, and then yield the current mode.
        typeof mode === 'number' && modeFor(this.map, mode, this.options);
        return this.map[modesKey];

    }

    /**
     * @method size
     * @return {Number}
     */
    size() {
        return polygons.get(this.map).size;
    }

    /**
     * @method all
     * @return {Array}
     */
    all() {
        return Array.from(polygons.get(this.map));
    }

    /**
     * @method cancel
     * @return {void}
     */
    cancel() {
        this.map[cancelKey]();
    }

    /**
     * @method listenForEvents
     * @param {Object} hammer
     * @param {Object} map
     * @param {Object} svg
     * @param {Object} options
     * @return {void}
     */
    listenForEvents(hammer, map, svg, options) {

        /**
         * @method mouseDown
         * @param {Object} event
         * @return {void}
         */
        const mouseDown = event => {

            if (this.isTouchDevice) {
                return;
            }

            if (!(map[modesKey] & CREATE)) {

                // Polygons can only be created when the mode includes create.
                return;

            }

            if (this.isTouchDevice) {
                // this.lines.push([event.srcEvent.x, event.srcEvent.y]);
            } else {
                this.lines.push([event.latlng.lat, event.latlng.lng]);
            }

            /**
             * @constant latLngs
             * @type {Set}
             */
            const latLngs = new Set();

            let isZoomEnd = false;

            // Create the line iterator and move it to its first `yield` point, passing in the start point
            // from the mouse down event.
            const lineIterator = this.createPath(svg, map.latLngToContainerPoint(event.latlng), options.strokeWidth);

            /**
             * @method mouseMove
             * @param {Object} event
             * @return {void}
             */
            const mouseMove = event => {
                if (this.isTouchDevice) {
                    return;
                }
            
                // Resolve the pixel point to the latitudinal and longitudinal equivalent.
                const currentEvent = event.originalEvent.touches && event.originalEvent.touches.length > 0 
                    ? event.originalEvent.touches[0] 
                    : event.originalEvent;
            
                const point = map.mouseEventToContainerPoint(currentEvent);          
                const latlng = map.containerPointToLatLng(point);
            
                this.lines.push([latlng.lat, latlng.lng]);
            
                // Push each lat/lng value into the points set.
                latLngs.add(latlng);
            
                // Invoke the generator by passing in the starting point for the path.
                lineIterator(new Point(point.x, point.y));

                const windowWidth = window.innerWidth;
                const windowHeight = window.innerHeight;
                const EDGE_THRESHOLD = 50; // pixels
                const MOVE_OFFSET = 10; // pixels to move the map
                const currentZoom = map.getZoom();

                if (currentZoom >= 8) {
                    // Check if the pointer is near the edges and adjust the map position
                    if (point.x < EDGE_THRESHOLD) {
                        map.panBy([-MOVE_OFFSET, 0]); // Move left
                        triggerZoomEnd();
                    } else if (point.x > windowWidth - EDGE_THRESHOLD) {
                        map.panBy([MOVE_OFFSET, 0]); // Move right
                        triggerZoomEnd();
                    }
                
                    if (point.y < EDGE_THRESHOLD) {
                        map.panBy([0, -MOVE_OFFSET]); // Move up
                        triggerZoomEnd();
                    } else if (point.y > windowHeight - EDGE_THRESHOLD) {
                        map.panBy([0, MOVE_OFFSET]); // Move down
                        triggerZoomEnd();
                    }
                }
                
            
                if (isZoomEnd) {
                    triggerZoomEnd();
                }

            };
            

            if (this.isTouchDevice) {
                // hammer.on('pan', mouseMove);
            } else {
                map.on('mousemove', mouseMove);
            }

            /**
             * @method mouseUp
             * @param {Boolean} [create = true]
             * @return {Function}
             */
            const mouseUp = (_, create = true) => {
                if (this.isTouchDevice) {
                    return;
                }

                // Remove the ability to invoke `cancel`.
                map[cancelKey] = () => {};

                if (this.isTouchDevice) {
                    // hammer.off('pan', mouseMove);
                    // hammer.off('tap', mouseDown);
                    // hammer.off('pinch', mouseDown);
                    // hammer.off('swipe', mouseDown);
                } else {
                    map.off('mouseup', mouseUp);
                    map.off('mousemove', mouseMove);
                    map.off('zoomstart', onZoomEnd);
                    map.off('zoomend', onZoomEnd);
                    'body' in document && document.body.removeEventListener('mouseleave', mouseUp);
                    'body' in document && document.body.removeEventListener('touchend', mouseUp);
                }
       
                // Clear the SVG canvas.
                svg.selectAll('*').remove();

                if (create) {

                    // ...And finally if we have any lat/lngs in our set then we can attempt to
                    // create the polygon.
                    latLngs.size && createFor(map, Array.from(latLngs), options);

                    // Finally invoke the callback for the polygon regions.
                    updateFor(map, 'create');

                    // Exit the `CREATE` mode if the options permit it.
                    options.leaveModeAfterCreate && this.mode(this.mode() ^ CREATE);
                     
                    this.lines = [];
                }

            };

            // Clear up the events when the user releases the mouse.
            if (this.isTouchDevice) {
                // hammer.on('panend', mouseUp);
            } else {
                map.on('mouseup', mouseUp);
                'body' in document && document.body.addEventListener('mouseleave', mouseUp);
                'body' in document && document.body.addEventListener('touchend', mouseUp);
            }

            // Setup the function to invoke when `cancel` has been invoked.
            map[cancelKey] = () => mouseUp({}, false);


            const triggerZoomEnd = () => {
                svg.selectAll('*').remove();
    
                map.drawLatLngs = this.lines;
                updateFor(map, 'create');
                this.lines = [];
                isZoomEnd = false;
            };

            const onZoomEnd = () => {
                isZoomEnd = true;
                triggerZoomEnd();
            };

            map.on('zoomend', onZoomEnd);
            map.on('zoomstart', onZoomEnd);
            
        };

        if (this.isTouchDevice) {
            // hammer.on('tap', mouseDown);
        } else {
            map.on('mousedown', mouseDown);
        }
    }

    mouseDown(event) {
        if (!(this.map[modesKey] & CREATE)) return;
        if (this.isTouchDevice) {
            // this.lines.push([event.srcEvent.x, event.srcEvent.y]);
        } else {
            this.lines.push([event.latlng.lat, event.latlng.lng]);
        }

        const latLngs = new Set();
        let isZoomEnd = false;

        const lineIterator = this.createPath(this.svg, this.map.latLngToContainerPoint(event.latlng), this.options.strokeWidth);

        if (this.isTouchDevice) {
            // this.hammer.on('pan', this.mouseMoveHandler);
        } else {
            this.map.on('mousemove', this.mouseMoveHandler);
        }

        this.map[cancelKey] = () => this.mouseUp({}, false);

        this.map.on('zoomend', () => isZoomEnd = true);
    }

    mouseMove(event) {
        const currentCoords = this.isTouchDevice ? event.pointers[0] : event.srcEvent;
        const point = { x: currentCoords.clientX, y: currentCoords.clientY };
        const latlng = this.map.containerPointToLatLng(point);
    
        // Store the current point and add it to the set
        this.lines.push([latlng.lat, latlng.lng]);
        const latLngs = new Set();
        latLngs.add(latlng);
    
        // Update the path
        this.createPath(this.svg, point, this.options.strokeWidth)(point);
    }

    mouseUp(_, create = true) {
        this.map[cancelKey] = () => {};
    
        if (this.isTouchDevice) {
            this.hammer.off('pan', this.mouseMoveHandler);
            this.hammer.off('tap', this.mouseDownHandler);
            this.hammer.off('pinch', this.mouseDownHandler);
            this.hammer.off('swipe', this.mouseDownHandler);
        } else {
            this.map.off('mouseup', this.mouseUpHandler);
            this.map.off('mousemove', this.mouseMoveHandler);
            document.body.removeEventListener('mouseleave', this.mouseUpHandler);
            document.body.removeEventListener('touchend', this.mouseUpHandler);
        }
    
        this.svg.selectAll('*').remove();
    
        if (create) {
            if (this.lines.length) {
                createFor(this.map, Array.from(latLngs), this.options);
                updateFor(this.map, 'create');
                if (this.options.leaveModeAfterCreate) {
                    this.mode(this.mode() ^ CREATE);
                }
                this.lines = [];
            }
        }
    }


    /**
     * @method createPath
     * @param {Object} svg
     * @param {Point} fromPoint
     * @param {Number} strokeWidth
     * @return {void}
     */
    createPath(svg, fromPoint, strokeWidth) {
        let lastPoint = fromPoint;

        const lineFunction = line().curve(curveMonotoneX).x(d => d.x).y(d => d.y);

        return toPoint => {
            const lineData = [ lastPoint, toPoint ];
            lastPoint = toPoint;
            // Draw SVG line based on the last movement of the mouse's position.
            svg.append('path').classed('leaflet-line', true)
                .attr('d', lineFunction(lineData)).attr('fill', 'none')
                .attr('stroke', 'black').attr('stroke-width', strokeWidth);
        };
    }

}

/**
 * @method freeDraw
 * @return {Object}
 */
export const freeDraw = options => {
    return new FreeDraw(options);
};

export { CREATE, EDIT, DELETE, APPEND, EDIT_APPEND, NONE, ALL } from './helpers/Flags';

if (typeof window !== 'undefined') {

    // Attach to the `window` as `FreeDraw` if it exists, as this would prevent `new FreeDraw.default` when
    // using the web version.
    window.FreeDraw = FreeDraw;
    FreeDraw.CREATE = CREATE;
    FreeDraw.EDIT = EDIT;
    FreeDraw.DELETE = DELETE;
    FreeDraw.APPEND = APPEND;
    FreeDraw.EDIT_APPEND = EDIT_APPEND;
    FreeDraw.NONE = NONE;
    FreeDraw.ALL = ALL;

}
